import {DeliveryOption} from '@/product/delivery-option';
import plural from '@/utilities/plural';

/**
 * Creates an array of helper options from settings and delivery options.
 * This allows us to configure the text for the helper options in the settings,
 * then map them to the delivery options for the product.
 */
export function createHelperOptions(
  helperTextOptions: string[],
  deliveryOptions: DeliveryOption[]
): HelperOption[] {
  const optionsLength = deliveryOptions.length;
  const lastFrequency =
    deliveryOptions[optionsLength - 1]?.sellingPlan?.orderIntervalFrequency ??
    1;

  return helperTextOptions
    .map((helperText, i) => {
      const deliveryOption = deliveryOptions[i];
      const isRange =
        i === helperTextOptions.length - 1 &&
        helperTextOptions.length < optionsLength;
      return createHelperOption(
        helperText,
        deliveryOption,
        isRange,
        lastFrequency
      );
    })
    .filter(Boolean) as HelperOption[]; // Remove null values
}

/**
 * Creates an object with the title and description for a helper option
 * @param helperText - The title of the helper option
 * @param deliveryOption - The delivery option to use for the description
 * @param isRange - Whether the helper option is a range of frequencies
 * @param lastFrequency - The last frequency in the range
 * @returns An object with the title and description for a helper option
 */
function createHelperOption(
  helperText: string,
  deliveryOption: DeliveryOption | undefined,
  isRange: boolean,
  lastFrequency: number
): HelperOption | null {
  if (!deliveryOption) return null;

  const frequency = deliveryOption.sellingPlan?.orderIntervalFrequency ?? 1;
  const unit = deliveryOption.sellingPlan?.intervalUnit ?? 'month';
  const unitPlural = plural(frequency, unit);
  const unitPluralCapitalized =
    unitPlural.charAt(0).toUpperCase() + unitPlural.slice(1);

  const description = isRange
    ? `Every ${frequency}-${lastFrequency} ${unitPluralCapitalized}`
    : `Every ${frequency} ${unitPluralCapitalized}`;

  return {
    title: helperText,
    description,
    frequency,
    unit,
  };
}

export type HelperOption = {
  title: string;
  description: string;
  frequency: number;
  unit: string;
};
